import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  User,
  CheckCircle,
  Clock,
  AlertTriangle,
  Lock,
  Calendar,
  CreditCard,
  MapPin,
  Cake
} from 'lucide-react';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// Calculate days remaining from expiry date
const calculateDaysRemaining = (expiryDate) => {
  if (!expiryDate) return null;
  
  let expiry;
  // Handle different date formats
  if (expiryDate.includes('/')) {
    const parts = expiryDate.split('/');
    if (parts[2].length === 4) {
      expiry = new Date(parts[2], parts[1] - 1, parts[0]); // DD/MM/YYYY
    } else {
      expiry = new Date(parts[0], parts[1] - 1, parts[2]); // MM/DD/YY
    }
  } else {
    expiry = new Date(expiryDate);
  }
  
  if (isNaN(expiry.getTime())) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get status config
const getVisaStatus = (daysRemaining, hasActionTaken) => {
  if (hasActionTaken) {
    return {
      status: 'action_taken',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/15',
      borderColor: 'border-emerald-500',
      cardBg: 'bg-gradient-to-br from-emerald-900/30 to-emerald-950/50',
      icon: CheckCircle,
      text: 'Action Taken',
      textEn: 'ACTION TAKEN'
    };
  }
  
  if (daysRemaining === null || daysRemaining > 10) {
    return {
      status: 'active',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      cardBg: 'bg-gradient-to-br from-[#1E293B] to-[#0F172A]',
      icon: CheckCircle,
      text: 'Active',
      textEn: 'ACTIVE'
    };
  }
  
  if (daysRemaining > 5) {
    return {
      status: 'expiring_soon',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/50',
      cardBg: 'bg-gradient-to-br from-amber-900/20 to-[#0F172A]',
      icon: Clock,
      text: 'Expiring Soon',
      textEn: 'EXPIRING SOON'
    };
  }
  
  return {
    status: 'expired',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/50',
    cardBg: 'bg-gradient-to-br from-red-900/20 to-[#0F172A]',
    icon: AlertTriangle,
    text: daysRemaining < 0 ? 'Expired' : 'Urgent',
    textEn: daysRemaining < 0 ? 'EXPIRED' : 'URGENT'
  };
};

// Get worker status icon
const getStatusIcon = (status) => {
  const icons = {
    'abroad': '✈️',
    'arrived': '✅',
    'runaway': '🚨',
    'noarrival': '❌',
    'settled': '💰'
  };
  return icons[status] || '📋';
};

// PIN Screen Component
const PinScreen = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visa-tracker/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      
      if (data.valid) {
        sessionStorage.setItem('visaTrackerPin', pin);
        onUnlock();
      } else {
        setError(true);
        setTimeout(() => setError(false), 2000);
        setPin('');
      }
    } catch (err) {
      // Fallback to hardcoded PIN for testing
      if (pin === '123456') {
        sessionStorage.setItem('visaTrackerPin', pin);
        onUnlock();
      } else {
        setError(true);
        setTimeout(() => setError(false), 2000);
        setPin('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#000716] to-[#0a1628] flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white tracking-widest mb-8">TADABER</h1>
        <div className="flex items-center justify-center gap-2 mb-6">
          <Lock className="h-5 w-5 text-white/60" />
          <p className="text-white/60">Enter PIN</p>
        </div>
        
        <Input
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="••••••"
          maxLength={6}
          className="w-72 h-14 text-center text-2xl tracking-[8px] bg-white/10 border-2 border-white/20 focus:border-[#F6C13B] text-white mb-4"
          data-testid="pin-input"
        />
        
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="w-72 h-12 bg-gradient-to-r from-[#F6C13B] to-[#E07B27] hover:opacity-90 text-white font-bold"
          data-testid="pin-submit-btn"
        >
          {loading ? 'Verifying...' : 'Enter'}
        </Button>
        
        {error && (
          <p className="text-red-400 text-sm mt-4 animate-pulse">
            Wrong PIN
          </p>
        )}
      </div>
    </div>
  );
};

// Main Visa Tracker Component
const VisaTrackerPage = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filter, setFilter] = useState('all');

  // Check if already unlocked
  useEffect(() => {
    const savedPin = sessionStorage.getItem('visaTrackerPin');
    if (savedPin) {
      setUnlocked(true);
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas/tracker`);
      if (!response.ok) {
        const fallback = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas`);
        const data = await fallback.json();
        setVisas(Array.isArray(data) ? data : []);
      } else {
        const data = await response.json();
        setVisas(Array.isArray(data) ? data : (data.visas || []));
      }
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading visa data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (unlocked) {
      loadData();
      const interval = setInterval(loadData, 30000);
      return () => clearInterval(interval);
    }
  }, [unlocked, loadData]);

  // Mark action taken
  const markActionTaken = async (visaId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas/${visaId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actionTaken: true,
          actionTakenDate: new Date().toISOString()
        })
      });
      loadData();
    } catch (error) {
      console.error('Error marking action:', error);
    }
  };

  // Filter visas
  const filteredVisas = visas.filter(v => {
    if (filter === 'all') return true;
    const days = calculateDaysRemaining(v.expiry_date);
    const hasAction = v.actionTaken || v.action_taken;
    
    if (filter === 'action_taken') return hasAction;
    if (filter === 'active') return !hasAction && (days === null || days > 10);
    if (filter === 'expiring') return !hasAction && days !== null && days > 5 && days <= 10;
    if (filter === 'expired') return !hasAction && days !== null && days <= 5;
    return true;
  });

  // Stats
  const stats = {
    total: visas.length,
    active: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date);
      return !v.actionTaken && !v.action_taken && (days === null || days > 10);
    }).length,
    expiring: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date);
      return !v.actionTaken && !v.action_taken && days !== null && days > 5 && days <= 10;
    }).length,
    expired: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date);
      return !v.actionTaken && !v.action_taken && days !== null && days <= 5;
    }).length,
    actionTaken: visas.filter(v => v.actionTaken || v.action_taken).length
  };

  if (!unlocked) {
    return <PinScreen onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#000716] via-[#0a1628] to-[#532a0a] text-white">
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold italic text-white mb-2">Visas</h1>
          <p className="text-white/60 text-sm">Visa Tracker</p>
        </div>

        {/* Auto Refresh Indicator */}
        <div className="flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-4">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-emerald-400 text-sm">Auto-refresh every 30 seconds</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`p-3 rounded-xl text-center transition-all ${
              filter === 'all' ? 'bg-white/20 border-2 border-white/40' : 'bg-white/5'
            }`}
          >
            <p className="text-lg font-bold text-white">{stats.total}</p>
            <p className="text-[10px] text-white/60">All</p>
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`p-3 rounded-xl text-center transition-all ${
              filter === 'active' ? 'bg-emerald-500/30 border-2 border-emerald-500' : 'bg-emerald-500/10'
            }`}
          >
            <p className="text-lg font-bold text-emerald-400">{stats.active}</p>
            <p className="text-[10px] text-emerald-400/70">Active</p>
          </button>
          <button
            onClick={() => setFilter('expiring')}
            className={`p-3 rounded-xl text-center transition-all ${
              filter === 'expiring' ? 'bg-amber-500/30 border-2 border-amber-500' : 'bg-amber-500/10'
            }`}
          >
            <p className="text-lg font-bold text-amber-400">{stats.expiring}</p>
            <p className="text-[10px] text-amber-400/70">Expiring</p>
          </button>
          <button
            onClick={() => setFilter('expired')}
            className={`p-3 rounded-xl text-center transition-all ${
              filter === 'expired' ? 'bg-red-500/30 border-2 border-red-500' : 'bg-red-500/10'
            }`}
          >
            <p className="text-lg font-bold text-red-400">{stats.expired}</p>
            <p className="text-[10px] text-red-400/70">Expired</p>
          </button>
        </div>

        {/* Action Taken Filter */}
        <button
          onClick={() => setFilter(filter === 'action_taken' ? 'all' : 'action_taken')}
          className={`w-full mb-4 p-3 rounded-xl text-center transition-all flex items-center justify-center gap-2 ${
            filter === 'action_taken' 
              ? 'bg-emerald-600 border-2 border-emerald-400' 
              : 'bg-emerald-500/10 border border-emerald-500/30'
          }`}
        >
          <CheckCircle className="h-4 w-4 text-emerald-400" />
          <span className="text-emerald-400 font-bold">Action Taken ({stats.actionTaken})</span>
        </button>

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-2xl bg-[#1E293B]" />
            ))}
          </div>
        ) : filteredVisas.length === 0 ? (
          <Card className="bg-[#1E293B]/50 border-white/10">
            <CardContent className="p-8 text-center">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-white/60">No visas in this category</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredVisas.map((visa) => (
              <VisaCard 
                key={visa.id} 
                visa={visa}
                onMarkAction={markActionTaken}
              />
            ))}
          </div>
        )}

        {/* Last Update */}
        {lastUpdate && (
          <div className="text-center mt-6 text-white/40 text-xs">
            Last updated: {lastUpdate.toLocaleTimeString('en-US')}
          </div>
        )}
      </div>
    </div>
  );
};

// Visa Card Component - Full details like old system
const VisaCard = ({ visa, onMarkAction }) => {
  const daysRemaining = calculateDaysRemaining(visa.expiry_date);
  const hasActionTaken = visa.actionTaken || visa.action_taken;
  const hasStatusChanged = visa.linkedWorkerProcessStatus === 'CHANGE_STATUS';
  const showStamp = hasActionTaken || hasStatusChanged;
  const statusConfig = getVisaStatus(daysRemaining, showStamp);
  const StatusIcon = statusConfig.icon;

  const daysText = daysRemaining === null || isNaN(daysRemaining)
    ? 'N/A'
    : Math.abs(daysRemaining);
    
  const daysLabel = daysRemaining === null || isNaN(daysRemaining)
    ? ''
    : daysRemaining >= 0 
      ? 'days remaining' 
      : 'days expired';

  // Get initials for photo placeholder
  const initials = (visa.passenger_name || 'UN').split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase();

  return (
    <Card className={`relative rounded-2xl overflow-visible border-2 ${statusConfig.borderColor} ${statusConfig.cardBg}`}>
      {/* Status Header */}
      <div className={`${statusConfig.bgColor} border-b ${statusConfig.borderColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
          <span className={`${statusConfig.color} text-xs font-bold`}>
            {showStamp ? 'STATUS CHANGED' : statusConfig.textEn}
          </span>
        </div>
        <span className="text-white/50 text-xs font-mono">
          #{(visa.visa_number || visa.id || '').substring(0, 12).toUpperCase()}
        </span>
      </div>

      {/* Alert Banner */}
      {showStamp && (
        <div className="bg-emerald-600 text-center py-2">
          <span className="text-white text-xs font-bold">✓ STATUS CHANGED - ACTION TAKEN</span>
        </div>
      )}
      {!showStamp && statusConfig.status === 'expiring_soon' && (
        <div className="bg-amber-600 text-center py-2">
          <span className="text-white text-xs font-bold">VISA EXPIRING SOON</span>
        </div>
      )}
      {!showStamp && statusConfig.status === 'expired' && (
        <div className="bg-red-600 text-center py-2">
          <span className="text-white text-xs font-bold">
            {daysRemaining < 0 ? 'VISA EXPIRED' : 'URGENT ACTION REQUIRED'}
          </span>
        </div>
      )}

      {/* Main Content */}
      <CardContent className={`p-4 relative ${showStamp ? 'opacity-80' : ''}`}>
        {/* Status Changed Stamp - Rectangular */}
        {showStamp && (
          <div className="mb-3 flex justify-center">
            <div className="border-4 border-emerald-500 rounded-lg px-6 py-2.5 bg-emerald-900/95 shadow-lg shadow-emerald-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
                <div className="flex flex-col items-start">
                  <span className="text-emerald-300 text-sm font-black tracking-wider">STATUS CHANGED</span>
                  <span className="text-emerald-300 text-xs font-bold tracking-wider">تم تغيير الحالة</span>
                  <span className="text-emerald-200 text-[10px] font-medium mt-0.5">
                    {visa.statusChangedAt || visa.actionTakenDate 
                      ? new Date(visa.statusChangedAt || visa.actionTakenDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() 
                      : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Person Info Row */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
            {visa.passenger_photo ? (
              <img src={formatBase64Image(visa.passenger_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#F6C13B] font-bold text-sm">{initials}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white truncate">{visa.passenger_name || 'Unknown'}</h3>
            <div className="flex items-center gap-2 text-xs text-white/50">
              {visa.passport_number && (
                <span className="bg-white/10 px-2 py-0.5 rounded font-mono">{visa.passport_number}</span>
              )}
              {visa.nationality && <span>{visa.nationality}</span>}
            </div>
          </div>
        </div>

        {/* Visa Details Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Expiry Date
            </div>
            <div className="text-sm font-medium text-white">{visa.expiry_date || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              Visa No.
            </div>
            <div className="text-sm font-medium text-white">{visa.visa_number || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1">Duration</div>
            <div className="text-sm font-medium text-white">{visa.duration_days || 'N/A'} days</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Country
            </div>
            <div className="text-sm font-medium text-white">{visa.visa_country || 'UAE'}</div>
          </div>
        </div>

        {/* Birth Date & Passport Expiry Row */}
        <div className="grid grid-cols-2 gap-2 mb-3 bg-[#0F172A]/40 rounded-lg p-3">
          <div className="text-center">
            <div className="text-[10px] text-white/50 mb-1 flex items-center justify-center gap-1">
              <Cake className="h-3 w-3" />
              Date of Birth
            </div>
            <div className="text-sm text-white/80">{visa.date_of_birth || 'N/A'}</div>
          </div>
          <div className="text-center border-l border-white/10">
            <div className="text-[10px] text-white/50 mb-1">Passport Expiry</div>
            <div className="text-sm text-amber-400">{visa.passport_expiry || 'N/A'}</div>
          </div>
        </div>

        {/* Days Counter */}
        {daysRemaining !== null && !isNaN(daysRemaining) && (
          <div className={`text-center py-3 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} mb-3`}>
            <div className={`text-3xl font-bold ${statusConfig.color}`}>{daysText}</div>
            <div className="text-[10px] text-white/50">{daysLabel}</div>
          </div>
        )}

        {/* Linked Worker Section */}
        {visa.linkedCaseId && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] text-emerald-400 font-bold uppercase">LINKED WORKER</div>
                <div className="text-sm text-white font-medium">{visa.linkedWorkerName || 'Unknown'}</div>
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                visa.linkedWorkerStatus === 'arrived' ? 'bg-emerald-500/20 text-emerald-400' :
                visa.linkedWorkerStatus === 'runaway' ? 'bg-red-500/20 text-red-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {getStatusIcon(visa.linkedWorkerStatus)} {(visa.linkedWorkerStatus || 'abroad').toUpperCase()}
              </span>
            </div>
            {visa.linkedWorkerProcessStatus && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white/50">Process:</span>
                <span className={`px-2 py-0.5 rounded ${
                  visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' 
                    ? 'bg-amber-500/20 text-amber-400' 
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' ? '🔄 CHANGE STATUS' : '✓ NORMAL'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Action Button */}
        {!hasActionTaken && (
          <Button
            onClick={() => onMarkAction(visa.id)}
            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark Action Taken
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default VisaTrackerPage;
